import { createContext, useEffect, useState } from "react";
import { getProfileApi, logoutApi, getAllAssignedModule } from "../api/global-api/api-auth.js";
import { useNavigate } from "react-router-dom";

export const AuthContext = createContext();

export default function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [assignedModules, setAssignedModules] = useState([]);
  const [allowedPaths, setAllowedPaths] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadingType, setLoadingType] = useState("refresh"); 

  const navigate = useNavigate();

  // 🔹 Flatten tree to paths
  const flattenModules = (modules) => {
    let result = [];

    modules.forEach((m) => {
      if (m.path) result.push(m.path);

      if (m.child_modules?.length) {
        result = result.concat(flattenModules(m.child_modules));
      }
    });

    return result;
  };
 

  useEffect(() => {
    const initAuth = async () => {
      try {
        // Profile and assigned modules don't depend on each other (both only
        // need the session cookie), so request them together, not one after
        // the other. Either failing still means "not logged in" (catch below).
        const [profileRes, moduleRes] = await Promise.all([
          getProfileApi(),
          getAllAssignedModule(),
        ]);
        const userData = profileRes.data;
        setUser(userData);

        const modules = moduleRes.data || [];

        setAssignedModules(modules);

        // 3️⃣ Generate allowed paths
        const paths = flattenModules(modules);
        setAllowedPaths(paths);

      } catch (error) {
        console.error("Auth init failed:", error);
        setUser(null);
      } finally {
        setLoading(false);
        // Bootstrap done — drop the post-login loader flag so it can't leak into
        // a later visit to the login page.
        sessionStorage.removeItem("sb_just_logged_in");
      }
    };

    initAuth();
  }, []);

  // 🔹 Re-fetch the logged-in user's profile (used after profile edits)
  const refreshProfile = async () => {
    try {
      const profileRes = await getProfileApi();
      setUser(profileRes.data);
      return profileRes.data;
    } catch (error) {
      console.error("Failed to refresh profile:", error);
      return null;
    }
  };

  const logout = async () => {
    try {
      await logoutApi();
    } catch (err) {
      console.error("Logout error:", err);
    } finally {
      setUser(null);
      setAssignedModules([]);
      setAllowedPaths([]);
      navigate("/login");
    }
  };

  return (
    <AuthContext.Provider
      value={{
        user,
        setUser,
        refreshProfile,
        loading,
        logout,
        assignedModules,
        allowedPaths,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
}

